import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createTheme, makeStyles } from '@material-ui/core/styles'; // createTheme is not used, can remove
import TextField from '@material-ui/core/TextField';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useRef } from 'react'; // Import useRef

const useStyles = makeStyles(theme => ({})); // Still empty, consider if you need it

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// searchId is not needed as a global variable, it's better to manage state locally
// let searchId = ''; // REMOVE THIS GLOBAL VARIABLE

export function EO2V2Autocomplete(props) {
  // console.log("444444444444444444444444444444444444", props.value); // Keep this for debugging parent props

  const classes = useStyles();
  // Initialize options with empty array, as data will be fetched
  const [options, setOptions] = React.useState({ values: [], totalCount: 0 });
  const [defaultOptions, setDefaultOptions] = React.useState({ values: [], totalCount: 0 });
  // Initialize 'value' state directly from props.value (which is an array of objects)
  const [value, setValue] = React.useState(props.multiple ? props.value || [] : props.value || null);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [searchText, setSearchText] = React.useState('');
  // displayKey is set as a prop, no need for internal state unless it can change dynamically
  // const [displayKey, setdisplayKey] = React.useState('name'); // REMOVE THIS
  const loading = false; // This 'loading' state might need to be managed dynamically for UX
  const initialLoadDone = useRef(false); // Flag to track initial data load

  // Utility to get nested display key (moved outside useEffect for reusability)
  const getDisplayValue = option => {
    if (!option) return '';
    if (props.displayKey && typeof option === 'object') {
      // Ensure option is an object
      return props.displayKey.split('.').reduce((a, prop) => (a && a[prop] !== undefined ? a[prop] : ''), option);
    }
    return option.name || ''; // Fallback to option.name
  };

  // Helper to resolve selected value objects against a list of options
  const resolveValueObjects = (targetValue, currentOptionsList) => {
    if (!targetValue) {
      return props.multiple ? [] : null; // Return appropriate empty state
    }

    if (props.multiple) {
      // If targetValue is an array of objects, filter currentOptionsList
      const targetIds = targetValue.map(item => item.id);
      return currentOptionsList.filter(opt => targetIds.includes(opt.id));
    } else {
      // If targetValue is a single object, find it in currentOptionsList
      const targetId = targetValue.id;
      return currentOptionsList.find(opt => opt.id === targetId);
    }
  };

  // --- Primary useEffect for initial data load and setting internal value state ---
  useEffect(() => {
    let isMounted = true; // Cleanup flag to prevent state updates on unmounted component

    // Skip if initial load is done and props.value hasn't deeply changed
    // Using a deep comparison for props.value
    if (initialLoadDone.current && JSON.stringify(props.value) === JSON.stringify(value)) {
      console.log('Skipping initial data fetch: Already loaded and value matches props.');
      return;
    }

    const fetchDataAndSetInitialValue = async () => {
      if (props.$dataSource) {
        // console.log('Fetching initial data for Autocomplete...');
        try {
          const resp = await props.$dataSource({ page: 0, size: 20 }, { type: 'initial', searchText: '' }).toPromise();

          if (isMounted) {
            // console.log('Initial Data Fetched:', resp.content);

            // Merge initial props.value into the fetched options list
            const mergedOptions = [...resp.content];
            let additionalValueCount = 0;

            if (props.multiple && Array.isArray(props.value)) {
              props.value.forEach(item => {
                if (item && item.id && !mergedOptions.some(opt => opt.id === item.id)) {
                  mergedOptions.unshift(item); // Add unique selected items to the front
                  additionalValueCount++;
                }
              });
            } else if (!props.multiple && props.value && props.value.id) {
              if (!mergedOptions.some(opt => opt.id === props.value.id)) {
                mergedOptions.unshift(props.value);
                additionalValueCount++;
              }
            }

            setOptions({ values: mergedOptions, totalCount: resp.totalElements + additionalValueCount });
            setDefaultOptions({ values: mergedOptions, totalCount: resp.totalElements + additionalValueCount });

            // Now that options are loaded, resolve and set the internal value state
            if (props.value) {
              const resolvedValue = resolveValueObjects(props.value, mergedOptions);
              // console.log('Resolved Value for internal state:', resolvedValue);
              setValue(resolvedValue);
            }
            initialLoadDone.current = true; // Mark initial load as complete
          }
        } catch (error) {
          console.error('Error fetching initial data for Autocomplete:', error);
          // Handle error, perhaps set options to props.value directly if API failed
          if (isMounted) {
            setOptions({ values: props.value || [], totalCount: props.value ? props.value.length : 0 });
            setDefaultOptions({ values: props.value || [], totalCount: props.value ? props.value.length : 0 });
            setValue(props.value || (props.multiple ? [] : null));
          }
        }
      } else {
        // If no dataSource prop is provided, but a value is given, set internal state directly
        if (isMounted && props.value) {
          setValue(props.value);
          // Ensure options also contain the value for getOptionLabel to work
          const initialOptions = Array.isArray(props.value) ? props.value : props.value ? [props.value] : [];
          setOptions({ values: initialOptions, totalCount: initialOptions.length });
          setDefaultOptions({ values: initialOptions, totalCount: initialOptions.length });
          initialLoadDone.current = true;
        }
      }
    };

    fetchDataAndSetInitialValue();

    // Cleanup function for useEffect
    return () => {
      isMounted = false;
    };
  }, [props.$dataSource, JSON.stringify(props.value), props.multiple]); // Dependencies: re-run if dataSource, value, or multiple prop changes

  // --- useEffect for parentToChildValues (for external updates/reloads of options) ---
  useEffect(() => {
    if (props.parentToChildValues) {
      // console.log('--- useEffect: parentToChildValues or changeDetect ---');
      const resp = props.parentToChildValues;
      const newOptions = [...resp.content];

      // Ensure existing selected values are not lost when parentToChildValues updates options
      if (value && Array.isArray(value)) {
        value.forEach(vItem => {
          if (vItem && vItem.id && !newOptions.some(opt => opt.id === vItem.id)) {
            newOptions.unshift(vItem); // Add selected items to the front
          }
        });
      } else if (value && value.id && !newOptions.some(opt => opt.id === value.id)) {
        newOptions.unshift(value);
      }

      setOptions({
        values: newOptions,
        totalCount: resp.totalElements + (Array.isArray(value) ? value.length : value ? 1 : 0),
      });
      setDefaultOptions({
        values: newOptions,
        totalCount: resp.totalElements + (Array.isArray(value) ? value.length : value ? 1 : 0),
      });

      // If `changeDetect` is intended to trigger a re-evaluation of the `value`
      // based on the new options (e.g., if the underlying data for the selected ID changed)
      // you might want to call resolveValueObjects again here.
      // If props.txtValue is not used to update the actual selected value, this block might be redundant.
      // if (!props.txtValue && props.value) {
      //    const resolvedValue = resolveValueObjects(props.value, newOptions);
      //    setValue(resolvedValue);
      // }
    }
  }, [props.changeDetect, props.parentToChildValues]); // Dependencies

  // --- Removed unused/redundant UseEffects based on the new logic ---
  // useEffect related to props.txtValue (its logic is covered by the primary useEffect now, or you need to clarify its purpose)
  // useEffect related to searchId = props.value; changeValue(); (its logic is covered by the primary useEffect)

  const loadMore = () => {
    if (options.values.length < options.totalCount) {
      // console.log('Loading more data...');
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      // Ensure props.$dataSource exists before subscribing
      if (props.$dataSource) {
        props.$dataSource({ page: nextPage }, { type: 'load', searchText }).subscribe(resp => {
          setOptions(prevOptions => ({
            ...prevOptions,
            values: [...prevOptions.values, ...resp.content],
          }));

          if (!searchText) {
            setDefaultOptions(prevOptions => ({
              ...prevOptions,
              values: [...prevOptions.values, ...resp.content],
            }));
          }
        });
      }
    }
  };

  const searchData = inputSearchText => {
    // console.log('Searching data for:', inputSearchText);
    setSearchText(inputSearchText);
    setCurrentPage(0); // Reset page on new search

    // Ensure props.$dataSource exists before subscribing
    if (props.$dataSource) {
      props.$dataSource({ page: 0 }, { type: 'search', searchText: inputSearchText }).subscribe(resp => {
        setOptions({ values: resp.content, totalCount: resp.totalElements });
        // After a search, the selected value might not be in the new options if it doesn't match the search.
        // Material-UI's Autocomplete handles this gracefully, but if you want to keep
        // the selected item visible even if it's not in the current search results,
        // you'd need to merge `value` into `options.values` here as well.
      });
    }
  };

  const handleChange = (e, newValue) => {
    // console.log('Autocomplete onChange (user interaction):', newValue);
    // newValue is already the full object(s) from the options list
    setValue(newValue || (props.multiple ? [] : null)); // Use null for single select to clear it
    props.onChange(e, newValue); // Pass the full objects back to the parent
  };

  const onClose = () => {
    // Reset options to default only if no search filter is active
    if (!searchText) {
      setOptions(defaultOptions);
    }
    setCurrentPage(0);
    setSearchText(''); // Clear search text on close
  };

  const autocompleteFilterChange = (options, state) => {
    // When fetching options from API based on search input,
    // you typically return all the options received from the API,
    // letting the API handle the filtering.
    // If you do client-side filtering *after* API fetch, you'd implement it here.
    return options; // Returning all options, assuming API already filtered
  };

  // console.log('🔧 Current Value (internal state):', value);
  // console.log('📦 Current Options (internal state):', options.values);
  // console.log(
  //   '✅ Matches (Value vs. Options):',
  //   (Array.isArray(value) ? value : (value ? [value] : []))
  //       .filter(vItem => (options.values || []).some(oItem => oItem?.id === vItem?.id))
  // );

  const getOptionsWithSelectAll = () => {
    if (props.multiple && options.values?.length > 0) {
      // Check if 'Select All' is already present
      const hasSelectAll = options.values.some(opt => opt.id === 'selectall');
      const valuesWithSelectAll = hasSelectAll
        ? options.values
        : [{ id: 'selectall', name: 'Select All' }, ...options.values];

      return valuesWithSelectAll;
    }
    return options.values ?? [];
  };

  return (
    // <Autocomplete
    //   ListboxProps={{
    //     onScroll: event => {
    //       const listboxNode = event.currentTarget;
    //       if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
    //         loadMore();
    //       }
    //     },
    //   }}
    //   multiple={props.multiple || false}
    //   id={props.id}
    //   name={props.name}
    //   value={value}
    //   onChange={handleChange}
    //   onClose={onClose}
    //   loading={loading}
    //   options={options.values ?? []}
    //   getOptionLabel={option => getDisplayValue(option)}
    //   filterOptions={autocompleteFilterChange}
    //   getOptionSelected={(option, value) => option.id === value?.id}
    //   renderOption={(option, { selected }) => {
    //     if (props.multiple) {
    //       const allSelected = false;
    //       const selectedOpt = (option.id === 'selectall' && allSelected) || selected;
    //       return (
    //         <div style={{ fontSize: '12px' }}>
    //           <Checkbox
    //             icon={icon}
    //             checkedIcon={checkedIcon}
    //             style={{ marginRight: 8, color: '#3a7cff' }}
    //             checked={selectedOpt}
    //           />
    //           {getDisplayValue(option)}
    //         </div>
    //       );
    //     } else {
    //       return <div style={{ fontSize: '12px' }}>{getDisplayValue(option)}</div>;
    //     }
    //   }}
    //   disabled={props.disabled}
    //   renderInput={params => (
    //     <TextField
    //       {...params}
    //       error={props.error}
    //       helperText={props.helperText}
    //       required={props.required}
    //       onChange={ev => {
    //         // dont fire API if the user delete or not entered anything
    //         if ((ev.target.value !== '' || ev.target.value !== null) && ev.target.value.length > 1) {
    //           searchData(ev.target.value);
    //         }
    //       }}
    //       label={props.label}
    //       ref={props.ref}
    //       InputProps={{
    //         ...params.InputProps,
    //         endAdornment: (
    //           <React.Fragment>
    //             {loading ? <CircularProgress color="inherit" size={20} /> : null}
    //             {params.InputProps.endAdornment}
    //           </React.Fragment>
    //         ),
    //       }}
    //     />
    //   )}
    // />
    <Autocomplete
      ListboxProps={{
        onScroll: event => {
          const listboxNode = event.currentTarget;
          if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
            loadMore();
          }
        },
      }}
      multiple={props.multiple || false} // Use props.multiple directly
      id={props.id} // Use props.id directly
      name={props.name} // Use props.name directly
      value={value} // Pass the internal `value` state
      onChange={handleChange}
      onClose={onClose}
      loading={loading}
      options={options.values ?? []} // Pass the internal `options.values` state
      getOptionLabel={getDisplayValue} // Use the helper function
      filterOptions={autocompleteFilterChange}
      // This is crucial: compares an option object with a value object
      isOptionEqualToValue={(option, val) => {
        // If val is null/undefined or option is null/undefined, they are not equal
        if (!option || !val) return false;
        return option.id === val.id;
      }}
      renderOption={(option, { selected }) => {
        if (props.multiple) {
          // Check if this specific option is currently in the internal `value` array
          const isSelected = Array.isArray(value) && value.some(vItem => vItem.id === option.id);
          return (
            <div style={{ fontSize: '12px' }}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8, color: '#3a7cff' }}
                checked={isSelected} // Use our `isSelected` check
              />
              {getDisplayValue(option)}
            </div>
          );
        } else {
          return <div style={{ fontSize: '12px' }}>{getDisplayValue(option)}</div>;
        }
      }}
      disabled={props.disabled}
      renderInput={params => (
        <TextField
          {...params}
          error={props.error}
          helperText={props.helperText}
          required={props.required}
          onChange={ev => {
            // Fire API if text length > 1 or if clearing the input
            if (ev.target.value.length > 1 || ev.target.value === '') {
              searchData(ev.target.value);
            }
          }}
          label={props.label}
          ref={props.ref}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}
